import { UserSchema } from '@chatbot/shared'
import { Alert, Box, Button, Card, CardContent, Typography } from '@mui/material'
import { GoogleLogin } from '@react-oauth/google'
import type React from 'react'
import { useEffect, useState } from 'react'
import { API_BASE_URL } from '../config'
import { useAuth } from '../contexts/AuthContext'
import { AddiumLogo } from './AddiumLogo'

export const LoginPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [googleReady, setGoogleReady] = useState(false)
  const { login } = useAuth()

  // Check if Google OAuth is ready
  useEffect(() => {
    const checkGoogleReady = () => {
      try {
        // Simple check to see if the Google OAuth context is available
        if (window.google?.accounts?.id) {
          setGoogleReady(true)
        } else {
          setTimeout(checkGoogleReady, 100)
        }
      } catch (_e) {
        setTimeout(checkGoogleReady, 100)
      }
    }

    // Start checking after a small delay to let the provider initialize
    setTimeout(checkGoogleReady, 500)
  }, [])

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setIsLoading(true)
    setError(null)

    try {
      // Validate token with backend
      const validateResponse = await fetch(`${API_BASE_URL}/auth/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: credentialResponse.credential,
        }),
      })

      if (!validateResponse.ok) {
        const errorData = await validateResponse.json()
        throw new Error(errorData.error || 'Authentication failed')
      }

      const rawData = await validateResponse.json()
      const validationResult = UserSchema.safeParse(rawData.user)

      if (!validationResult.success) {
        throw new Error('Invalid user data received from server.')
      }

      const userData = validationResult.data

      // Store token and call login - let routing handle the redirect
      localStorage.setItem('auth_token', credentialResponse.credential)
      login(credentialResponse.credential, userData)
    } catch (err) {
      console.error('Login error:', err)
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleError = () => {
    console.error('Google Sign-In failed')
    setError('Google Sign-In failed. Please try again.')
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.50',
        p: 2,
      }}
    >
      <Card
        elevation={2}
        sx={{
          maxWidth: 400,
          width: '100%',
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <AddiumLogo height={40} />
          </Box>

          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
            Welcome
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {error}
            </Alert>
          )}

          {/* Google Sign-In Button */}
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              justifyContent: 'center',
              opacity: isLoading ? 0.5 : 1,
              pointerEvents: isLoading ? 'none' : 'auto',
            }}
          >
            {googleReady ? (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                size="large"
                width="300"
              />
            ) : (
              <Button disabled variant="outlined" sx={{ width: '300px', height: '44px' }}>
                Loading Google Sign-In...
              </Button>
            )}
          </Box>

          {isLoading && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Signing you in...
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            Only Addium organization members can access this service
          </Typography>
        </CardContent>
      </Card>
    </Box>
  )
}

// Extend window interface for Google Sign-In
declare global {
  interface Window {
    google: any
  }
}
